import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, MouseEvent as ReactMouseEvent } from 'react';

/**
 * A popup placed from its trigger's measured box.
 *
 * `position: fixed` from its trigger's measured box, flipped above the trigger when
 * there is no room below, and clamped 12px from every viewport edge -- the way
 * `Niraxo_bid_rbac.html` places its role picker.
 *
 * It closes on an outside press, on Escape, and when its trigger leaves the viewport. It does **not**
 * close because something scrolled: a page scroll re-measures and moves it, and a scroll inside the
 * popup is ignored entirely. Nothing here reacts to focus or blur.
 *
 * <h2>Why it lives here</h2>
 *
 * It was `features/rbac/hooks/`, with its own note saying it belonged in `shared/` and asking whoever
 * needed it next to move it rather than add a third copy. The sidebar's entity picker was that third
 * caller, so it moved: generic UI plumbing inside a feature cannot be reached from outside it, because
 * `@/features/**` deep imports are an ESLint error by design.
 *
 * **`features/masters` still carries its own.** That one is not quite this one — it places a row action
 * menu by clamping and everything else by flipping, where this always flips — so adopting it there is a
 * change to how eighteen masters' popups sit, and wants verifying against the reference rather than
 * assuming. It is the remaining copy; there is no longer a reason to make another.
 */

/** Gap the reference leaves between a trigger and its popup. */
const GAP = 6;

/** Margin kept between a popup and the viewport edge. */
const EDGE = 12;

interface AnchorRect {
  left: number;
  right: number;
  top: number;
  bottom: number;
  width: number;
}

export interface AnchoredPopupOptions {
  width: number;
  height: number;
  /** `end` right-aligns the popup to its trigger; `start` left-aligns it. */
  align: 'start' | 'end';
  zIndex: number;
}

export interface AnchoredPopupResult {
  open: boolean;
  style: CSSProperties;
  popupRef: React.RefObject<HTMLDivElement | null>;
  toggle: (event: ReactMouseEvent<HTMLElement>) => void;
  close: () => void;
}

function placement(
  rect: AnchorRect | null,
  { width, height, align, zIndex }: AnchoredPopupOptions,
): CSSProperties {
  const base: CSSProperties = { position: 'fixed', width, zIndex };

  /* Never measured: centred rather than dropped in the corner. */
  if (!rect) {
    return { ...base, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }

  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;
  const flipUp = rect.bottom + GAP + height > viewportHeight - EDGE;

  const left =
    align === 'end'
      ? Math.max(EDGE, rect.right - width)
      : Math.min(Math.max(EDGE, rect.left), Math.max(EDGE, viewportWidth - width - EDGE));

  return {
    ...base,
    left,
    top: flipUp ? Math.max(EDGE, rect.top - GAP - height) : rect.bottom + GAP,
    maxHeight: Math.max(160, Math.min(height, viewportHeight - EDGE * 2)),
    overflowY: 'auto',
  };
}

export function useAnchoredPopup(options: AnchoredPopupOptions): AnchoredPopupResult {
  const [rect, setRect] = useState<AnchorRect | null>(null);
  const [open, setOpen] = useState(false);

  const popupRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  /** Reads the trigger's box now. The single place a rect is produced. */
  const measure = useCallback((trigger: HTMLElement): AnchorRect => {
    const box = trigger.getBoundingClientRect();
    return {
      left: box.left,
      right: box.right,
      top: box.top,
      bottom: box.bottom,
      width: box.width,
    };
  }, []);

  const toggle = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      const trigger = event.currentTarget;

      triggerRef.current = trigger;
      setRect(measure(trigger));
      setOpen((previous) => !previous);
    },
    [measure],
  );

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event: Event) => {
      const target = event.target as Node | null;
      if (target === null) return;
      if (popupRef.current?.contains(target) === true) return;
      if (triggerRef.current?.contains(target) === true) return;
      setOpen(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };

    /*
      The popup is `position: fixed` at coordinates measured once, so it has to answer when the
      viewport moves under it. It FOLLOWS its trigger rather than closing.

      It used to close, and that is the bug this hook was reported for. `scroll` does not bubble from
      an element, so the only way to notice an ancestor scroll container is to listen in the CAPTURE
      phase -- which also delivers the popup's OWN scroll events here. The popup scrolls (it is given
      a `maxHeight` and `overflow-y: auto` below), so the first wheel tick inside the list, or the
      first pixel of a scrollbar drag, closed the very list the user was reading.

      Filtering those events out would have been enough to stop the symptom, but re-measuring is what
      the position actually needs: a page scroll moves the trigger, and a popup that merely survives
      it would sit detached from its button.
    */
    let frame = 0;

    const onViewportChange = (event: Event) => {
      /* A scroll inside the popup moves nothing; there is no new position to compute. */
      const target = event.target as Node | null;
      if (target !== null && popupRef.current?.contains(target) === true) return;

      /* One re-measure per frame: scroll fires far faster than the screen repaints. */
      if (frame !== 0) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;

        const trigger = triggerRef.current;
        if (!trigger) return;

        const box = measure(trigger);
        /*
          The trigger has left the viewport -- scrolled out of its own container, say. There is
          nothing left to anchor to, so this is the one case that still closes.
        */
        if (box.bottom < 0 || box.top > window.innerHeight) {
          setOpen(false);
          return;
        }
        setRect(box);
      });
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onViewportChange, true);
    window.addEventListener('resize', onViewportChange);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onViewportChange, true);
      window.removeEventListener('resize', onViewportChange);
      if (frame !== 0) window.cancelAnimationFrame(frame);
    };
  }, [measure, open]);

  return { open, style: placement(rect, options), popupRef, toggle, close };
}
